import React, { useEffect, useState, useCallback } from 'react';
import { menuApi, MenuItem } from '../api/client';
import { MenuItemModal } from '../components/MenuItemModal';
import { CategoryModal } from '../components/CategoryModal';
import { AuthState } from '../types';

interface Props {
  auth: AuthState;
}

export const MenuManagerPage: React.FC<Props> = ({ auth }) => {
  const { restaurantId } = auth;

  const [categories, setCategories] = useState<string[]>([]);
  const [itemsByCategory, setItemsByCategory] = useState<Record<string, MenuItem[]>>({});
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modals
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | undefined>();
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [renamingCategory, setRenamingCategory] = useState<string | undefined>();
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null); // itemId

  const loadMenu = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await menuApi.list(restaurantId);
      setCategories(data.categories);
      setItemsByCategory(data.byCategory);
      if (!activeCategory && data.categories.length > 0) {
        setActiveCategory(data.categories[0]);
      }
    } catch {
      setError('Failed to load menu.');
    } finally {
      setLoading(false);
    }
  }, [restaurantId, activeCategory]);

  useEffect(() => { void loadMenu(); }, []);

  // ── Category CRUD ───────────────────────────────────────────────────────────

  const handleAddCategory = async (name: string) => {
    if (categories.includes(name)) return;
    const updated = [...categories, name];
    await menuApi.updateCategories(restaurantId, updated);
    setCategories(updated);
    setActiveCategory(name);
    setShowCategoryModal(false);
  };

  const handleRenameCategory = async (newName: string) => {
    if (!renamingCategory) return;
    const updated = categories.map((c) => (c === renamingCategory ? newName : c));
    await menuApi.updateCategories(restaurantId, updated, renamingCategory, newName);
    setCategories(updated);
    setItemsByCategory((prev) => {
      const next = { ...prev };
      next[newName] = (next[renamingCategory] || []).map((i) => ({ ...i, category: newName }));
      delete next[renamingCategory];
      return next;
    });
    if (activeCategory === renamingCategory) setActiveCategory(newName);
    setRenamingCategory(undefined);
    setShowCategoryModal(false);
  };

  const handleDeleteCategory = async (cat: string) => {
    const updated = categories.filter((c) => c !== cat);
    await menuApi.updateCategories(restaurantId, updated);
    setCategories(updated);
    if (activeCategory === cat) setActiveCategory(updated[0] || '');
  };

  const moveCategory = async (cat: string, dir: -1 | 1) => {
    const idx = categories.indexOf(cat);
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= categories.length) return;
    const updated = [...categories];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    await menuApi.updateCategories(restaurantId, updated);
    setCategories(updated);
  };

  // ── Item CRUD ───────────────────────────────────────────────────────────────

  const handleItemSaved = (saved: MenuItem) => {
    setItemsByCategory((prev) => {
      const cat = saved.category;
      const existing = prev[cat] || [];
      const idx = existing.findIndex((i) => i.itemId === saved.itemId);
      const updated = idx >= 0
        ? existing.map((i) => (i.itemId === saved.itemId ? saved : i))
        : [...existing, saved];
      return { ...prev, [cat]: updated };
    });
    setShowItemModal(false);
    setEditingItem(undefined);
  };

  const handleDeleteItem = async (item: MenuItem) => {
    await menuApi.delete(restaurantId, item.itemId);
    setItemsByCategory((prev) => ({
      ...prev,
      [item.category]: (prev[item.category] || []).filter((i) => i.itemId !== item.itemId),
    }));
    setDeleteConfirm(null);
  };

  const toggleAvailability = async (item: MenuItem) => {
    const updated = await menuApi.update(restaurantId, item.itemId, { isAvailable: !item.isAvailable });
    handleItemSaved(updated);
  };

  const items = itemsByCategory[activeCategory] || [];

  return (
    <div
      className="flex bg-gray-950 overflow-hidden"
      // Height = viewport minus the top nav (~57px). `dvh` handles mobile
      // browser chrome showing/hiding without jank.
      style={{ height: 'calc(100dvh - 57px)' }}
    >
      {/* ── Left Sidebar: Categories ── */}
      <aside className="w-60 flex-shrink-0 border-r border-white/8 bg-gray-900/50 flex flex-col">
        <div className="p-4 border-b border-white/8">
          <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Categories</h2>
        </div>

        <div className="flex-1 overflow-y-auto py-2">
          {categories.map((cat) => (
            <div
              key={cat}
              className={`group flex items-center gap-2 px-4 py-3 cursor-pointer transition-colors ${
                activeCategory === cat
                  ? 'bg-indigo-600/20 border-r-2 border-indigo-500'
                  : 'hover:bg-white/5'
              }`}
              onClick={() => setActiveCategory(cat)}
            >
              <span className={`flex-1 text-sm font-semibold truncate ${activeCategory === cat ? 'text-indigo-300' : 'text-gray-300'}`}>
                🍽 {cat}
              </span>
              <span className="text-xs text-gray-600 mr-1">{(itemsByCategory[cat] || []).length}</span>

              {/* Category actions */}
              <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                <button
                  onClick={(e) => { e.stopPropagation(); moveCategory(cat, -1); }}
                  className="text-gray-500 hover:text-white text-xs px-1"
                  title="Move up"
                >↑</button>
                <button
                  onClick={(e) => { e.stopPropagation(); moveCategory(cat, 1); }}
                  className="text-gray-500 hover:text-white text-xs px-1"
                  title="Move down"
                >↓</button>
                <button
                  onClick={(e) => { e.stopPropagation(); setRenamingCategory(cat); setShowCategoryModal(true); }}
                  className="text-gray-500 hover:text-indigo-400 text-xs px-1"
                  title="Rename"
                >✎</button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat); }}
                  className="text-gray-500 hover:text-red-400 text-xs px-1"
                  title="Delete category"
                >✕</button>
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-white/8">
          <button
            onClick={() => { setRenamingCategory(undefined); setShowCategoryModal(true); }}
            className="w-full py-2 rounded-xl border border-dashed border-indigo-500/50 text-indigo-400 hover:bg-indigo-500/10 text-sm font-semibold transition-colors"
          >
            + Add Category
          </button>
        </div>
      </aside>

      {/* ── Main: Items Grid ── */}
      <main className="flex-1 p-6 overflow-y-auto">
        {/* Toolbar */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-white">{activeCategory || 'Menu'}</h1>
            <p className="text-gray-500 text-sm">{items.length} item{items.length !== 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={() => { setEditingItem(undefined); setShowItemModal(true); }}
            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-sm transition-colors shadow-lg shadow-indigo-500/20"
          >
            + Add Item
          </button>
        </div>

        {/* Loading / Error */}
        {loading && <div className="flex items-center gap-3 text-gray-400"><div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"/><span>Loading menu…</span></div>}
        {error && <p className="text-red-400 bg-red-500/10 rounded-xl p-4">{error}</p>}

        {/* No category selected */}
        {!loading && categories.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="text-6xl mb-4">🍽</div>
            <h3 className="text-white text-lg font-bold mb-2">No categories yet</h3>
            <p className="text-gray-500 mb-6">Add your first category using the sidebar to get started</p>
          </div>
        )}

        {/* Items grid */}
        {!loading && items.length === 0 && activeCategory && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-white text-lg font-bold mb-2">No items in {activeCategory}</h3>
            <p className="text-gray-500 mb-6">Click "Add Item" to create the first item in this category</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((item) => (
            <div
              key={item.itemId}
              className={`bg-gray-900 rounded-2xl border overflow-hidden group transition-all ${
                item.isAvailable ? 'border-white/8' : 'border-white/5 opacity-60'
              }`}
            >
              {/* Image */}
              <div className="h-36 bg-indigo-500/10 flex items-center justify-center overflow-hidden relative">
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-5xl">🍔</span>
                )}
                {!item.isAvailable && (
                  <div className="absolute inset-0 bg-gray-900/60 flex items-center justify-center">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Hidden</span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-bold text-white text-sm leading-tight line-clamp-2">{item.name}</h3>
                  <span className="text-indigo-400 font-bold text-sm flex-shrink-0">${item.price.toFixed(2)}</span>
                </div>
                {item.description && (
                  <p className="text-gray-500 text-xs line-clamp-2 mb-2">{item.description}</p>
                )}
                {item.calories && <p className="text-gray-600 text-xs mb-2">{item.calories} kcal</p>}
                {item.tags && item.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {item.tags.map((t) => (
                      <span key={t} className="text-xs px-2 py-0.5 rounded bg-indigo-500/15 text-indigo-400">{t}</span>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t border-white/5">
                  <button
                    onClick={() => toggleAvailability(item)}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                      item.isAvailable
                        ? 'bg-green-500/10 text-green-400 hover:bg-green-500/20'
                        : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    {item.isAvailable ? '✓ Visible' : '◌ Hidden'}
                  </button>
                  <button
                    onClick={() => { setEditingItem(item); setShowItemModal(true); }}
                    className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-indigo-500/20 text-gray-400 hover:text-indigo-400 text-xs font-semibold transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(item.itemId)}
                    className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 text-gray-400 hover:text-red-400 text-xs font-semibold transition-colors"
                  >
                    🗑
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* ── Modals ── */}
      {showItemModal && (
        <MenuItemModal
          restaurantId={restaurantId}
          item={editingItem}
          categories={categories}
          onSave={handleItemSaved}
          onClose={() => { setShowItemModal(false); setEditingItem(undefined); }}
        />
      )}

      {showCategoryModal && (
        <CategoryModal
          initial={renamingCategory}
          title={renamingCategory ? `Rename "${renamingCategory}"` : 'New Category'}
          onSave={renamingCategory ? handleRenameCategory : handleAddCategory}
          onClose={() => { setShowCategoryModal(false); setRenamingCategory(undefined); }}
        />
      )}

      {/* Delete confirm dialog */}
      {deleteConfirm && (() => {
        const item = items.find((i) => i.itemId === deleteConfirm);
        if (!item) return null;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
            <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-sm space-y-4">
              <h3 className="text-white font-bold text-lg">Delete "{item.name}"?</h3>
              <p className="text-gray-400 text-sm">This cannot be undone. The item will be removed from the kiosk immediately.</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-3 rounded-xl border border-white/10 text-gray-400 hover:text-white font-semibold">
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteItem(item)}
                  className="flex-1 py-3 rounded-xl bg-red-500 hover:bg-red-400 text-white font-bold transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};
